import { useCallback } from "react";

import { Building, LogOut, User } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { useOpenUrl } from "../../../hooks/useOpenUrl";
import {
  useAuth,
  useOrganization,
  useUser,
} from "../../Auth/UnifiedAuthProvider";
import { useNeutralino } from "../../Neutralino/NeutralinoContextProvider";
import { AppSettingsCard } from "./AppSettingsCard";

export const AppSettingsUI = () => {
  const isDesktop = useIsDesktop();
  const hasRealtimeAPI = useFeatureFlag("realtime-api");
  const { logout } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const openUrl = useOpenUrl();

  return (
    <div className="flex h-full flex-col space-y-6 overflow-y-auto p-4">
      {/* Account Settings Section */}
      <div>
        <h2 className="mb-4 text-lg font-medium">Account</h2>
        <p className="mb-4 text-sm text-gray-400">
          Manage your account and organization settings
        </p>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>User Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Name</div>
                <p className="text-base">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : (user?.firstName ??
                      user?.email?.split("@")[0] ??
                      "No name available")}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Email</div>
                <p className="text-base">
                  {user?.email ?? "No email available"}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building className="h-5 w-5" />
                <span>Organization</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Name</div>
                <p className="text-base">
                  {organization?.name ?? "No organization"}
                </p>
              </div>
              <p className="text-xs text-gray-500 pt-2">
                To switch organizations, please log out and log back in after
                changing your active organization in the dashboard.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* App Settings Section */}
      <div>
        <h2 className="mb-4 text-lg font-medium">App settings</h2>
        <p className="mb-4 text-sm text-gray-400">
          Configure how you use the Frontier X app.
        </p>

        <div className="flex flex-col space-y-4">
          <AlwaysOnTopSwitch enabled={isDesktop} />
          <UseRealtimeAPISwitch enabled={!!hasRealtimeAPI} />
        </div>
      </div>

      <Separator />

      {/* Logout Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Log out</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-gray-600">
            Sign out of your account. You'll be redirected to the login page.
          </p>
          <Button
            onClick={logout}
            variant="destructive"
            className="flex items-center space-x-2"
          >
            <LogOut className="h-4 w-4" />
            <span>Log out</span>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const AlwaysOnTopSwitch = ({
  enabled,
}: Readonly<{ enabled: boolean }>) => {
  const { isAlwaysOnTop, toggleAlwaysOnTop } = useNeutralino();
  const isDesktop = useIsDesktop();

  const handleSwitch = useCallback(() => {
    console.log("handleSwitch");
    toggleAlwaysOnTop();
  }, [toggleAlwaysOnTop]);

  return (
    <AppSettingsCard
      title="Always on top"
      description="Make the desktop application sit on top of all other windows."
    >
      <Switch
        checked={isDesktop && isAlwaysOnTop}
        onCheckedChange={handleSwitch}
        disabled={!enabled}
      />
    </AppSettingsCard>
  );
};

export const UseRealtimeAPISwitch = ({
  enabled,
}: Readonly<{ enabled: boolean }>) => {
  const [useRealtimeAPI, setUseRealtimeAPI] = useLocalStorage(
    "use-realtime-api",
    false,
  );
  return (
    <AppSettingsCard
      title="Use Realtime API"
      description="Enable the Realtime API to receive updates in real-time."
    >
      <Switch
        checked={useRealtimeAPI}
        onCheckedChange={() => {
          setUseRealtimeAPI(!useRealtimeAPI);
        }}
        disabled={!enabled}
      />
    </AppSettingsCard>
  );
};
